import { useContext } from 'react';
import { WebAPIContext } from '../../contexts/WebAPIContext';

/** スプレッドシートテンプレート情報の型定義 */
export type SpreadsheetTemplateType = {
  templateId: string;
  templateName: string;
  templateLink: string;
  updateDate?: string;
};

/** API レスポンスの型定義 */
export type SpreadsheetTemplateAPIResponse = {
  status: 'success' | 'failed' | 'expired';
  message: string;
};

/**
 * スプレッドシートテンプレートAPI用カスタムフック
 */
export const useSpreadsheetTemplateAPI = () => {
  const WebAPI = useContext(WebAPIContext);

  /**
   * レスポンス処理
   */
  const handleResponse = (result: any) => {
    if (result.errors?.length > 0) {
      throw new Error(result.errors[0].message || 'GraphQL error');
    }
    const data = Object.values(result.data || {})[0];
    if ((data as any)?.status === 'failed') {
      throw new Error((data as any).message || 'API returned failure');
    }
    return data;
  };

  /**
   * スプレッドシートテンプレート一覧を取得する
   */
  const getSpreadsheetTemplate = async (): Promise<SpreadsheetTemplateType[]> => {
    const result = await WebAPI.request({
      query: `
        query {
          getSpreadsheetTemplate {
            templateId
            templateName
            templateLink
            updateDate
          }
        }`,
    });
    return handleResponse(result) as SpreadsheetTemplateType[];
  };

  /**
   * スプレッドシートテンプレートを新規登録する
   */
  const putSpreadsheetTemplate = async (input: { templateName: string; templateLink: string }): Promise<SpreadsheetTemplateAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation putSpreadsheetTemplate($input: putSpreadsheetTemplateInfo) {
          putSpreadsheetTemplate(input: $input) {
            message
            status
          }
        }
      `,
      variables: { input },
    });
    return handleResponse(result) as SpreadsheetTemplateAPIResponse;
  };

  /**
   * スプレッドシートテンプレートを更新する
   */
  const updateSpreadsheetTemplate = async (input: { templateId: string; templateName: string; templateLink: string; updateDate?: string }): Promise<SpreadsheetTemplateAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation updateSpreadsheetTemplate($input: updateSpreadsheetTemplateInfo!) {
          updateSpreadsheetTemplate(input: $input) {
            message
            status
          }
        }
      `,
      variables: { input },
    });
    return handleResponse(result) as SpreadsheetTemplateAPIResponse;
  };

  /**
   * スプレッドシートテンプレートを削除する
   */
  const deletedSpreadsheetTemplate = async (input: { templateId: string; updateDate?: string }): Promise<SpreadsheetTemplateAPIResponse> => {
    const result = await WebAPI.request({
      query: `
        mutation deletedSpreadsheetTemplate($input: deletedSpreadsheetTemplateInfo!) {
          deletedSpreadsheetTemplate(input: $input) {
            message
            status
          }
        }`,
      variables: {
        input,
      },
    });
    return handleResponse(result) as SpreadsheetTemplateAPIResponse;
  };

  return {
    getSpreadsheetTemplate,
    putSpreadsheetTemplate,
    updateSpreadsheetTemplate,
    deletedSpreadsheetTemplate,
  };
};
